import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { listApprovals } from "../../api/agentApi";

interface ApprovalSummary {
  id: string;
  workflow_run_id: string;
  step_id: string;
  title: string;
  prompt: string;
  status: string;
  created_at: string | null;
  expires_at: string | null;
  responded_at: string | null;
  responded_by: string | null;
}

export default function ApprovalsListPage() {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState<ApprovalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>("all");

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusFilter = selectedTab === "all" ? null : selectedTab;
      const data = await listApprovals(statusFilter);
      setApprovals(data.approvals || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getStatusColor = (status: string, expiresAt: string | null) => {
    if (isExpired(expiresAt)) return "error";
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getEmptyMessage = () => {
    switch (selectedTab) {
      case "pending":
        return {
          title: "No pending approvals",
          subtitle:
            "When a workflow reaches a human input step, approval requests will appear here.",
        };
      case "approved":
        return {
          title: "No approved requests",
          subtitle: "Approved approval requests will appear here.",
        };
      case "rejected":
        return {
          title: "No rejected requests",
          subtitle: "Rejected approval requests will appear here.",
        };
      case "expired":
        return {
          title: "No expired requests",
          subtitle: "Expired approval requests will appear here.",
        };
      default:
        return {
          title: "No approvals",
          subtitle:
            "When a workflow reaches a human input step, approval requests will appear here.",
        };
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Approvals
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Human-in-the-loop approval requests for your workflows
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchApprovals}
          disabled={loading}
          sx={{ borderRadius: "99px", textTransform: "none" }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          aria-label="approval status tabs"
        >
          <Tab label="All" value="all" sx={{ textTransform: "none" }} />
          <Tab label="Pending" value="pending" sx={{ textTransform: "none" }} />
          <Tab label="Approved" value="approved" sx={{ textTransform: "none" }} />
          <Tab label="Rejected" value="rejected" sx={{ textTransform: "none" }} />
          <Tab label="Expired" value="expired" sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {loading && approvals.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : approvals.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {getEmptyMessage().title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {getEmptyMessage().subtitle}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Step
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Created
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Expires
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Responded At
                </TableCell>
                <TableCell
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Responded By
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ bgcolor: "#F8FAFC", fontWeight: 600, py: 1.5 }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvals.map((approval) => {
                const expired = isExpired(approval.expires_at);
                const isPending = approval.status === "pending" && !expired;
                return (
                  <TableRow
                    key={approval.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/workflow/approvals/${approval.id}`)
                    }
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        {approval.title || "Approval Required"}
                      </Typography>
                      {approval.prompt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "12px",
                            mt: 0.25,
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {approval.prompt}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "12px",
                          color: "#475569",
                        }}
                      >
                        {approval.step_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expired ? "expired" : approval.status}
                        size="small"
                        color={getStatusColor(approval.status, approval.expires_at)}
                        variant="outlined"
                        sx={{ fontSize: "11px" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#475569" }}>
                      {approval.created_at
                        ? new Date(approval.created_at).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#475569" }}>
                      {approval.expires_at
                        ? new Date(approval.expires_at).toLocaleString()
                        : "No expiry"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#475569" }}>
                      {approval.responded_at
                        ? new Date(approval.responded_at).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#475569" }}>
                      {approval.responded_by || "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/workflow/approvals/${approval.id}`);
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          borderRadius: "99px",
                          px: 2,
                        }}
                      >
                        {isPending ? "Review" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
